import { getReleaseStageFromHostname } from './get-release-stage-from-hostname';

import initSegment from './init-segment';
import initBugsnag from './init-bugsnag';

const EVENTS = {
  INFORMATION: 'Information',
  ERROR: 'Error',
  PERFORMANCE: 'Performance',
  STEPTHROUGH: 'Stepthrough Entered',
  GLOBAL_ADD: 'Global Add Button Action',
  RECORD_LIST_SCREEN: {
    SAVED_FILTERS: {
      APPLIED: 'Saved Filter applied',
      CREATED: 'Saved Filter created',
      FAVOURITED: 'Saved Filter favourited'
    }
  },
  NAVIGATION_MENU_TOGGLE: {
    OPEN: 'Navigation Menu Toggle Open',
    CLOSED: 'Navigation Menu Toggle Closed'
  },
  RELEASE_NOTES: {
    OPEN: 'Release notes opened'
  },
  PAYWALL: {
    EMAIL_SUPPORT: 'Paywall Support Email',
    LEARN_MORE: 'Paywall Learn More'
  },
  FEATURE_FLAGS: {
    ENABLED: 'Feature Flag Enabled',
    DISABLED: 'Feature Flag Disabled'
  },
  ESIGN: {
    REQUEST_SENT: 'E-sign Request Sent',
    TEMPLATE_ADDED: 'E-sign Template Added'
  },
  CALENDAR: {
    // Adding load page as an event so Intercom knows how many times a user
    // has visited calendar (which we can't do otherwise :C)
    LOAD_PAGE: 'Visited Calendar',
    ADD_CONTACT_GUEST: 'Calendar Contact Guest/s Added',
    ADD_USER_GUEST: 'Calendar User Guest/s Added',
    CREATE_APPOINTMENT: 'Calendar Create Appointment',
    CONNECT_EXTERNAL: 'Calendar Connect External',
    DISCONNECT_EXTERNAL: 'Calendar Disconnect External',
    CREATE_CALENDAR: 'Calendar Create New Calendar',
    CREATE_FOLLOWUP_REMINDER: 'Create appointment with follow up reminder',
    GUEST_CONFIRMATION_SENT: 'Confirmation sent (guest)',
    VENDOR_CONFIRMATION_SENT: 'Confirmation sent (listing owner)',
    TENANT_CONFIRMATION_SENT: 'Confirmation sent (tenant)'
  },
  DASHBOARDS: {
    // Adding load page as an event so Intercom knows how many times a user
    // has visited the dashboards page (which we can't do otherwise :C)
    LOAD_PAGE: 'Visited Dashboards',
    OPEN_SWITCHER: 'Open Dashboard Switcher',
    CHANGE_DATE_RANGE: 'Change Dashboards Date Range',
    CHANGE_USER_FILTER: 'Change Dashboard User Filter',
    CHANGE_SOLD_STATUS_FILTER: 'Change Dashboard Sold Status Filter',
    CHANGE_CATEGORY_FILTER: 'Change Dashboard Category Filter',
    IDLE_REFRESH: 'Idle Refresh Dashboard'
  },
  CHAINS: {
    ADD_NOTE: 'Add Chain Note',
    OPEN_EDIT_CHAIN_DIALOG: 'Open Edit Chain Dialog'
  },
  PREVIEW_POPOUTS: {
    SEND_EMAIL: 'Send email from preview popout',
    SEND_SMS: 'Send SMS from preview popout',
    ADD_NOTE: 'Add note from preview popout',
    ADD_REMINDER: 'Add reminder from preview popout',
    ADD_FEEDBACK: 'Add feedback from preview popout',
    ADD_APPOINTMENT: 'Add appointment from preview popout',
    MAKE_CALL: 'Make click to dial call from preview popout'
  },
  LEADS_DIALOG: {
    OPEN: 'Open leads dialog',
    SEND_EMAIL_ACTION: 'Send email from leads dialog',
    SEND_SMS_ACTION: 'Send SMS from leads dialog',
    ADD_NOTE_ACTION: 'Add note from leads dialog',
    ADD_FEEDBACK_ACTION: 'Add feedback from leads dialog',
    UPDATE_ASSIGNEE: 'Change lead assignee from leads dialog',
    MARK_AS_COMPLETE: 'Mark as complete from leads dialog',
    BOOK_APPOINTMENT_ACTION: 'Book appointment from leads dialog',
    ADD_REMINDER_ACTION: 'Add reminder from leads dialog',
    LEAD_EDITED: 'Lead edited',
    ADD_MISSING_RECORDS_ACTION: 'Leads missing records banner clicked'
  },
  CUSTOM_REPORTING: {
    LOAD_PAGE: 'Custom report loaded',
    MODIFY_DATE_FILTER: 'Custom report date filter modified',
    MODIFY_USER_FILTER: 'Custom report user filter modified',
    MODIFY_LOCATION_FILTER: 'Custom report location filter modified',
    OPEN_COLUMN_PANEL: 'Custom report columns panel opened',
    OPEN_FILTER_PANEL: 'Custom report filters panel opened',
    ENABLE_PIVOT: 'Custom report pivot table enabled',
    EXPORT_DATA: 'Custom report data exported',
    OPEN_CONTEXT_MENU: 'Custom report context menu opened',
    CREATE_REPORT: 'Custom report created',
    UPDATE_REPORT: 'Custom report updated'
  },
  GLOBAL_SEARCH: {
    SELECTED_ITEM_FROM_APP_SEARCH: 'Selected item from app search',
    ABANDONED_SEARCH: 'Abandonded app search',
    NEW_SEARCH_FEEDBACK: 'Feedback on new search experience'
  },
  PIPELINES: {
    GROUP_MODE_CHANGED: 'Pipeline group mode changed',
    ORDER_BY_CHANGED: 'Pipeline order by changed',
    ITEM_DRAGGED: 'Pipeline item dragged'
  },
  AI_AD_TEXT: {
    AI_AD_TEXT_OPENED: 'AI Ad Text - Opened',
    AI_AD_TEXT_CLOSED: 'AI Ad Text - Closed',
    AI_AD_TEXT_INSERTED: 'AI Ad Text - Inserted',
    AI_AD_TEXT_LEARN_MORE: 'AI Ad Text - Learn More',
    AI_AD_TEXT_FAILED: 'AI Ad Text - Failed'
  },
  AI_COMMUNICATIONS: {
    AI_COMMUNICATIONS_OPENED: 'AI Communications - Opened',
    AI_COMMUNICATIONS_CLOSED: 'AI Communications - Closed',
    AI_COMMUNICATIONS_INSERTED: 'AI Communications - Inserted',
    AI_COMMUNICATIONS_FAILED: 'AI Communications - Failed'
  },
  AI_CONTENT: {
    AI_CONTENT_TONE_ADJUSTED: 'AI Content - Tone Adjusted',
    AI_CONTENT_AUDIENCE_ADJUSTED: 'AI Content - Audience Adjusted',
    AI_CONTENT_CUSTOM_REFINEMENT: 'AI Content - Custom Refinement',
    AI_CONTENT_SHORTER: 'AI Content - Shorter',
    AI_CONTENT_LONGER: 'AI Content - Longer',
    AI_CONTENT_GENERATE_NEW: 'AI Content - Generate New'
  },
  AI_FILTERS: {
    AI_FILTERS_OPENED: 'AI Filters - Opened',
    AI_FILTERS_GENERATED: 'AI Filters - Generated',
    AI_FILTERS_FAILED: 'AI Filters - Failed',
    AI_FILTERS_CLOSED: 'AI Filters - Closed',
    AI_FILTERS_FEEDBACK: 'AI Filters - Feedback'
  },
  NEWSLETTERS: {
    NEWSLETTERS_CREATED: 'Newsletters - Created'
  },
  PORTAL_STATS: {
    REACH_ACCOUNT_SETUP_LINK_CLICKED: 'Reach Account Setup Link Clicked',
    REACH_LAUNCH_CAMPAIGN_CLICKED: 'Launch Reach Campaign Clicked',
    REACH_LEARN_MORE_CLICKED: 'Reach - Learn More Clicked'
  },
  BUYER_MANAGEMENT: {
    VIEW_OPENED: 'Buyer Management View Opened',
    CONTACT_DRAWER_OPENED: 'Contact Drawer Opened',
    ROW_BUYER_NAME_CLICKED: 'Buyer Management Table Buyer Row Clicked',
    ROW_LISTING_NAME_CLICKED: 'Buyer Management Table Listing Row Clicked',
    ROW_DROPDOWN_OPENED: 'Row Dropdown Opened', // not implemented yet
    ROW_ACTION_ADD_FEEDBACK_CLICKED: 'Add Feedback Clicked',
    ROW_ACTION_ADD_NOTE_CLICKED: 'Add Note Clicked',
    ROW_ACTION_SEND_EMAIL_CLICKED: 'Send Email Clicked',
    ROW_ACTION_BOOK_APPOINTMENT_CLICKED: 'Book Appointment Clicked',
    ROW_ACTION_MARK_AS_CONTRACT_SENT_CLICKED: 'Mark As Contract Sent Clicked',
    ROW_ACTION_UNMARK_AS_CONTRACT_SENT_CLICKED:
      'Unmark As Contract Sent Clicked',
    ROW_ACTION_SEND_DOCUMENT_CLICKED: 'Send Document Clicked', // not implemented yet
    MERGE_MENU_OPENED: 'Merge Menu Opened',
    MERGE_EMAIL_CLICKED: 'Merge Email Clicked',
    MERGE_SMS_CLICKED: 'Merge SMS Clicked',
    MERGE_LETTER_CLICKED: 'Merge Letter Clicked',
    FILTER_TOTAL_BUYERS_CLICKED: 'Filter Total Buyers Clicked',
    FILTER_HOT_BUYERS_CLICKED: 'Filter Hot Buyers Clicked',
    FILTER_WARM_BUYERS_CLICKED: 'Filter Warm Buyers Clicked',
    FILTER_COLD_BUYERS_CLICKED: 'Filter Cold Buyers Clicked',
    FILTER_ENQUIRIES_CLICKED: 'Filter Enquiries Clicked',
    FILTER_ATTENDEES_CLICKED: 'Filter Attendees Clicked',
    FILTER_SECOND_INSPECTIONS_CLICKED: 'Filter Second Inspections Clicked',
    FILTER_OFFER_FEEDBACK_CLICKED: 'Filter Offer Feedback Clicked',
    FILTER_CONTRACT_SENT_CLICKED: 'Filter Contract Sent Clicked',
    TABLE_SORT_CLICKED: 'Table Sort Clicked',
    CONTACT_DRAWER_EMAIL_CLICKED: 'Contact Drawer Email Clicked',
    CONTACT_DRAWER_SMS_CLICKED: 'Contact Drawer SMS Clicked',
    CONTACT_DRAWER_FEEDBACK_CLICKED: 'Contact Drawer Feedback Clicked',
    CONTACT_DRAWER_NOTE_CLICKED: 'Contact Drawer Note Clicked',
    CONTACT_DRAWER_REMINDER_ADD_CLICKED: 'Contact Drawer Reminder Add Clicked',
    CONTACT_DRAWER_BOOK_APPOINTMENT_CLICKED:
      'Contact Drawer Book Appointment Clicked',
    CONTACT_DRAWER_OPEN_CONTACT_FULL_TAB_CLICKED:
      'Contact Drawer Open Contact Full Tab Clicked',
    CONTACT_DRAWER_PROPERTY_OWNED_CLICKED:
      'Contact Drawer Property Owned Clicked',
    CONTACT_DRAWER_TRACK_EDIT_CLICKED: 'Contact Drawer Track Edit Clicked',
    CONTACT_DRAWER_TRACK_ADD_CLICKED: 'Contact Drawer Track Add Clicked',
    CONTACT_DRAWER_INTERESTED_LISTINGS_TAB_CLICKED:
      'Contact Drawer Interested Listings Tab Clicked',
    CONTACT_DRAWER_ACTIVITY_TAB_CLICKED: 'Contact Drawer Activity Tab Clicked',
    CONTACT_DRAWER_FILTER_PROPERTY_CLICKED:
      'Contact Drawer Filter Property Clicked',
    HOME_FOLLOW_UP_STREAM_VIEWED: 'Home Follow-ups Stream Viewed',
    FOLLOW_UP_ADD_FEEDBACK_CLICKED: 'Follow-up Add Feedback Clicked',
    FOLLOW_UP_INDIVIDUAL_FEEDBACK_SAVED:
      'Follow-up Individual Feedback Saved (Done Clicked)',
    FOLLOW_UP_LISTING_ADDRESS_CLICKED: 'Follow-up Listing Address Clicked',
    FOLLOW_UP_CONTACT_NAME_CLICKED: 'Follow-up Contact Name Clicked'
  },
  REPORTS: {
    MENU_OPENED: 'Reports Menu Opened',
    LISTING_DETAIL_CLICKED: 'Reports Listing Detail Clicked',
    VENDOR_REPORT_CLICKED: 'Reports Vendor Report Clicked',
    VENDOR_REPORT_PREVIEW_CLICKED: 'Vendor Report Preview Clicked',
    VENDOR_REPORT_DOWNLOAD_CLICKED: 'Vendor Report Download Clicked', // in classic
    VENDOR_REPORT_EMAIL_CLICKED: 'Vendor Report Email Clicked' // in classic
  },
  REX_HUB: {
    PREVIEW_ON_REX_HUB_CLICKED: 'Preview on Rex Hub Clicked',
    SHARE_REX_HUB_LOGIN_CLICKED: 'Share Rex Hub Login Clicked',
    SHARE_REX_HUB_SEND_INVITE_CLICKED: 'Share Rex Hub Send Invite Clicked',
    SHARE_REX_HUB_DISABLE_ACCESS_TOGGLED: 'Share Rex Hub Disable Access Toggled'
  },
  TRACK_SETTINGS: {
    VIEW_OPENED: 'Track Settings View Opened',
    CREATE_TRACK_CLICKED: 'Track Settings Create Track Clicked',
    TRACK_CREATED: 'Track Settings Track Created',
    DUPLICATE_CLICKED: 'Track Settings Duplicate Clicked',
    TRACK_DUPLICATED: 'Track Settings Duplicated',
    EDIT_OPENED: 'Track Settings Edit Opened',
    SHARE_PERMISSIONS_UPDATED: 'Track Settings Share Permissions Updated',
    TRACK_SAVED: 'Track Settings Track Saved',
    HIDE_CLICKED: 'Track Settings Hide Clicked',
    REMINDER_ADD_CLICKED: 'Track Reminder Add Clicked',
    REMINDER_CREATED: 'Track Settings Reminder Created',
    REMINDER_MERGE_TEMPLATE_ATTACHED:
      'Track Settings Reminder Merge Template Attached'
  },
  TRACK: {
    ADD_TRACK_CLICKED: 'Add Track Clicked', // hard coded in reminders.js
    TRACK_ADDED: 'Track Added', // hard coded in reminders.js
    ACTIVE_DIALOG_MODAL_OPENED: 'Active Track Modal Opened',
    REASSIGN_CLICKED: 'Track Reassign Clicked',
    REMINDER_COMPLETED: 'Track Reminder Completed',
    CANCEL_CLICKED: 'Track Cancel Clicked',
    RESCHEDULE_CLICKED: 'Track Reschedule Clicked'
  },
  AUTOMATION_RULES: {
    VIEW_OPENED: 'Automation Rules Settings View Opened',
    EDIT_OPENED: 'Automation Rules Edit Opened',
    // creating a new automation from scratch does not exist yet:
    // CREATE_AUTOMATION_CLICKED: 'Automation Rules Create Automation Clicked',
    AUTOMATION_CREATED: 'Automation Rules Automation Created',
    DUPLICATE_CLICKED: 'Automation Rules Duplicate Clicked',
    AUTOMATION_DUPLICATED: 'Automation Rules Automation Duplicated',
    AUTOMATION_DISABLED: 'Automation Rules Automation Disabled',
    AUTOMATION_ENABLED: 'Automation Rules Automation Enabled'
  },
  PROSPECTOR: {
    PAGE_LOADED: 'Prospector Page Loaded',
    FILTER_TAB_SWITCHED: 'Prospector Filter Tab Switched',
    URGENT_FILTER_TOGGLED: 'Prospector Urgent Filter Toggled',
    TAGS_FILTER_CHANGED: 'Prospector Tags Filter Changed',
    CONTACT_CARD_VIEWED: 'Prospector Contact Card Viewed',
    LOG_CALL_CLICKED: 'Prospector Log Call Clicked',
    PHONE_NUMBER_COPIED: 'Prospector Phone Number Copied',
    CALL_OUTCOME_SELECTED: 'Prospector Call Outcome Selected',
    CALL_CHECKLIST_TOGGLED: 'Prospector Call Checklist Toggled',
    CALL_SAVED: 'Prospector Call Saved',
    CALL_CANCELLED: 'Prospector Call Cancelled',
    EMAIL_MODAL_OPENED: 'Prospector Email Modal Opened',
    EMAIL_SENT: 'Prospector Email Sent',
    SMS_MODAL_OPENED: 'Prospector SMS Modal Opened',
    SMS_SENT: 'Prospector SMS Sent',
    CONTACT_DISMISSED: 'Prospector Contact Dismissed',
    CONTACT_DISMISS_UNDONE: 'Prospector Contact Dismiss Undone',
    CONTACT_SNOOZED: 'Prospector Contact Snoozed',
    CONTACT_SNOOZE_UNDONE: 'Prospector Contact Snooze Undone',
    WHY_CONTACT_NOW_EXPANDED: 'Prospector Why Contact Now Expanded',
    CONTACT_NAME_CLICKED: 'Prospector Contact Name Clicked',
    CONTACT_DRAWER_ACTION_CLICKED: 'Prospector Contact Drawer Action Clicked',
    SESSION_SUMMARY: 'Prospector Session Summary',
    UPSELL_VIEWED: 'Prospector Upsell Viewed',
    UPSELL_CTA_CLICKED: 'Prospector Upsell CTA Clicked',
    UPSELL_DEMO_CLICKED: 'Prospector Upsell Demo Clicked',
    // The map surface (CRM-14829). The first events this table has carried for
    // it — everything above belongs to the list view — so they are named for the
    // surface as well as the action, and both carry `{ filter, value }` so the
    // two rows can be read together.
    MAP_OWNER_STATUS_FILTER_CHANGED:
      'Prospector Map Owner Status Filter Changed',
    MAP_PROPERTY_STATUS_FILTER_CHANGED:
      'Prospector Map Property Status Filter Changed',
    // The Filters popout: key names and an Advanced count, never values.
    MAP_FILTERS_APPLIED: 'Prospector Map Filters Applied',
    MAP_FILTERS_CLEARED: 'Prospector Map Filters Cleared',
    // The Data Source axis, offered only while the RP Data gate
    // is open — same `{ filter, value }` shape as its two siblings above.
    MAP_DATA_SOURCE_FILTER_CHANGED: 'Prospector Map Data Source Filter Changed',
    // The property data panel. Names and keys only: never a link url, which
    // carries the user's OAuth token.
    PROPERTY_DATA_PANEL_OPENED: 'Prospector Property Data Panel Opened',
    PROPERTY_DATA_PANEL_TAB_VIEWED: 'Prospector Property Data Panel Tab Viewed',
    PROPERTY_DATA_PANEL_LINK_CLICKED:
      'Prospector Property Data Panel Link Clicked',
    PROPERTY_DATA_PANEL_REPORT_OPENED:
      'Prospector Property Data Panel Report Opened',
    PROPERTY_DATA_PANEL_IMPORT_STARTED:
      'Prospector Property Data Panel Import Started',
    PROPERTY_DATA_PANEL_IMPORT_COMPLETED:
      'Prospector Property Data Panel Import Completed'
  },
  // AI Assist (CRM-15085) — the feature's first analytics instrumentation.
  // PANEL_OPENED_MANUAL/PANEL_CLOSED_MANUAL cover the sparkle-icon toggle;
  // PANEL_OPENED_AUTO plus the engaged/dismissed pair below distinguish the
  // weekly auto-open from a manual one and measure whether it led to a
  // message being sent.
  AI_ADMIN: {
    PANEL_OPENED_MANUAL: 'AI Admin Panel Opened Manual',
    PANEL_CLOSED_MANUAL: 'AI Admin Panel Closed Manual',
    PANEL_OPENED_AUTO: 'AI Admin Panel Opened Auto',
    AUTO_OPEN_ENGAGED: 'AI Admin Auto Open Engaged',
    AUTO_OPEN_DISMISSED_NO_ENGAGEMENT:
      'AI Admin Auto Open Dismissed No Engagement',
    // Home's suggested prompts measure which prompts get tried; the recent
    // chat click is the baseline for shrinking Recent Chats from three to one.
    SUGGESTED_PROMPTS_SHOWN: 'AI Admin Suggested Prompts Shown',
    SUGGESTED_PROMPT_CLICKED: 'AI Admin Suggested Prompt Clicked',
    SUGGESTED_PROMPTS_SCROLLED: 'AI Admin Suggested Prompts Scrolled',
    RECENT_CHAT_CLICKED: 'AI Admin Recent Chat Clicked'
  },
  // snake_case on purpose, unlike the rest: they match Rex PM's `sync_log_*` events so one
  // dashboard covers both apps' sync log pages. Never carry ids, names or `detail`.
  BT_SYNC_LOGS: {
    LIST_VIEWED: 'sync_log_list_viewed',
    DETAILS_VIEWED: 'sync_log_details_viewed',
    FILTER_APPLIED: 'sync_log_filter_applied'
  }
};

// TODO: refactor/genericize this to allow authapp/group-app/check-in-app to just call this
// TODO: maybe allow an array of the third parties we want to call - just incase we want different setup per application
const initThirdPartyScripts = (config, store, sessionModel) => {
  const { SEGMENT_ID } = config;

  initSegment(SEGMENT_ID).then(() => {
    store.dispatch(sessionModel.actionCreators.initSegment());
  });

  initBugsnag(config);
};

export {
  getReleaseStageFromHostname,
  EVENTS,
  initSegment,
  initBugsnag,
  initThirdPartyScripts
};
